import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

export const StatsSection = () => {
  const indicators = [
    { value: '20+', label: 'Års erfaring' },
    { value: 'Før / efter', label: 'Energimålinger' },
    { value: 'Intelligent', label: 'Lysstyring' },
    { value: 'Driftssikker', label: 'Implementering' },
    { value: 'Dokumenterbar', label: 'ESG-effekt' },
  ];

  return (
    <section 
      data-testid="stats-section" 
      className="py-24 md:py-32 bg-[#F4F2EE]"
    >
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Text content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <p className="text-[#A6B8A2] text-sm mb-4">
              Om Dilling EL
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl lg:text-5xl font-medium text-[#1E2A24] leading-tight mb-6">
              Specialister i energioptimering af erhvervsbygninger
            </h2>
            <p className="text-[#1E2A24]/60 leading-[1.8] mb-8">
              Hos Dilling EL arbejder vi med energioptimering og intelligent belysning i erhvervsbygninger, hvor belysning ofte udgør en væsentlig del af energiforbruget. Vi hjælper virksomheder med at reducere energiforbrug, forbedre lysforhold og skabe mere effektive installationer gennem moderne LED-løsninger og intelligent styring. Effekten dokumenteres gennem før- og eftermålinger, så virksomheden får konkrete data på energibesparelse og effektreduktion. Vi samarbejder tæt med drift, facility management, økonomi og ESG-ansvarlige, som har brug for gennemsigtige løsninger og dokumentation der kan bruges internt og i rapportering. Vores arbejde bygger på faste teams, tydelig planlægning og driftssikre løsninger, der kan implementeres uden unødige forstyrrelser af den daglige drift.
            </p>
            <Link 
              to="/om-os"
              className="inline-flex items-center gap-2 text-[#1E2A24] hover:text-[#0E3D24] transition-colors group"
            >
              <span className="text-sm">Læs mere om Dilling EL</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {/* Right - Indicators */}
          <motion.div 
            className="grid grid-cols-2 gap-8 md:gap-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={containerVariants}
          >
            {indicators.map((indicator, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center md:text-left"
              >
                <span className={`block font-['Cormorant_Garamond'] font-medium text-[#0E3D24] ${
                  index === 0 ? 'text-5xl md:text-6xl lg:text-7xl' : 'text-2xl md:text-3xl lg:text-4xl'
                }`}>
                  {indicator.value}
                </span>
                <span className="text-[#1E2A24]/50 text-sm mt-2 block">
                  {indicator.label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
